import { debounce } from 'lodash';
import { makeStyles } from '@mui/styles'
import React, { useState, useEffect } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Dialog, Skeleton,Typography } from '@mui/material'

import { createPersonalizedChallenge, getCompaniesWithDetails, getSubTopicsWithDetails } from 'services'
import { FileUploadSection } from 'components/FileUploadSection'
import { SimpleSelect } from 'components/CustomSelectFields'
import CustomButton from 'components/CustomButton'
import TextField from 'components/CustomFields';
import { capitalizeFirstLetter } from 'utils'
import { CompanyCard } from './Companies';
import { useServices, useSnackbar } from 'contexts';
import { AssessmentEntryPoint, ChallengeDifficulty } from './constant';
import StartAssessmentDialog from 'dialogs/StartAssessmentDialog';
import TitleTooltip from 'components/TitleTooltip';
import NoAccessTooltip from 'components/NoAccessTooltip';
import { useSelectedBatchRemoved } from 'announcementStore';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    boxShadow: "0px 0px 4px 0px #00000026",
    borderRadius: '4px',
  },
  header: {
    padding: theme.spacing(7, 10),
    width: '100%',
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  createContainer: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(9.5),
    width: '750px',
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(5, 6, 5, 6),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
    position: 'relative'
  },
  detailsSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
    position: 'relative'
  },
  blurDetails: {
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
  uploadsRow: {
    display: 'flex',
    gap: '16px',
  },
  dialogContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(4),
    height: '100%'
  },
  topicContainer: {
    maxHeight: '200px',
    overflow: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(5)
  },
  chip: {
    width: '198px',
    height: '40px',
    maxWidth: '198px',
    borderRadius: theme.spacing(5),
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    padding: theme.spacing(2, 4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer'
  },
  title: {
    borderBottom: '1px solid #8692A3',
    paddingBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  bottomBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(5),
    width: '100%',
    bottom: 0,
    left: 0,
    padding: theme.spacing(5, 0),
    backgroundColor: theme.palette.shades['clr-white-900']
  },
  companies: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    margin: '20px 0px'
  }
}))

const PersonalizedDetailsForm = ({ setActiveStep }) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();
  const removed = useSelectedBatchRemoved();

  const [companyName, setCompanyName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState({
    jd: null,
    resume: null
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedChipIndex, setSelectedChipIndex] = useState(null);
  const [chipsData, setChipsData] = useState([]);
  const [difficulty, setDifficulty] = useState(null);
  const [difficultyOptions, setDifficultyOptions] = useState([]);
  const [companiesData, setCompaniesData] = useState([]);
  const [startInterview , setStartInterview] = React.useState(false);
  const [challenge, setChallenge] = React.useState({});

  useEffect(() => {
    const fetchSubtopics = async () => {
      try {
        setLoading(true);
        const result = await service.callService(getSubTopicsWithDetails);
        const topicsArray = Object.values(result);

        let finalTopics = {};

        topicsArray.forEach((topic,i)=>{
          let challengeAvailable = 0;
          Object.keys(ChallengeDifficulty).forEach((difficulty,j)=>{
            if(topic[difficulty][1]>0 && (topic[difficulty][0]!==topic[difficulty][1])){
              challengeAvailable+=1;
            }
          });
          if(challengeAvailable){
            finalTopics[topic.name] = topic;
          }
        });

        setChipsData(finalTopics);
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubtopics();
  }, []);

  useEffect(() => {
    if (selectedChipIndex !== null) {
      const selectedChip = Object.values(chipsData)[selectedChipIndex];
      updateDifficultyOptions(selectedChip);
    }
  }, [selectedChipIndex, chipsData]);

  const updateDifficultyOptions = (chipData) => {
    if (!chipData) return;

    const availableDifficulties = [];
    
    if (chipData.very_easy && chipData.very_easy[1] > 0 && (chipData.very_easy[0] !== chipData.very_easy[1])) {
      availableDifficulties.push({ _id: 'very_easy', name: 'Very easy' });
    }
    if (chipData.easy && chipData.easy[1] > 0 && (chipData.easy[0] !== chipData.easy[1])) {
      availableDifficulties.push({ _id: 'easy', name: 'Easy' });
    }
    
    if (chipData.medium && chipData.medium[1] > 0 && (chipData.medium[0] !== chipData.medium[1])) {
      availableDifficulties.push({ _id: 'medium', name: 'Medium' });
    }
    
    if (chipData.hard && chipData.hard[1] > 0 && (chipData.hard[0] !== chipData.hard[1])) {
      availableDifficulties.push({ _id: 'hard', name: 'Hard' });
    }

    // If no difficulties are available, provide default options
    if (availableDifficulties.length === 0) {
      availableDifficulties.push(
        { _id: 'very_easy', name: 'Very easy' },
        { _id: 'easy', name: 'Easy' },
        { _id: 'medium', name: 'Medium' },
        { _id: 'hard', name: 'Hard' }
      );
    }

    setDifficultyOptions(availableDifficulties);
    setDifficulty(null); // Reset difficulty when topic changes
  };

  const handleFileProcessed = ({ fileName, scrapedData, fileType }) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fileType]: {
        fileName,
        scrapedData
      }
    }));
  };

  const handleChipClick = (index) => {
    setSelectedChipIndex(index);
  };

  const getRandomDifficulty = () => {
    if (difficultyOptions.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * difficultyOptions.length);
    return difficultyOptions[randomIndex]._id;
  };

  const handleCreate = async () => {
    try{

      const challenge = await createPersonalizedChallenge({
        company: companyName,
        subTopic: Object.values(chipsData)[selectedChipIndex]._id,
        difficulty: difficulty._id
      });

      setChallenge(challenge);
      setStartInterview(true);
    }catch(err){
      console.log(err);
      snackbar.error('Something went wrong!')
    }
  }

  const handleNextClick = () => {
    let selectedDifficulty = difficulty;
    
    // If no difficulty is selected, choose a random one
    if (!selectedDifficulty) {
      selectedDifficulty = getRandomDifficulty();
    }
    
    setOpenDialog(true);
    
    console.log("Ready to proceed with:", {
      company: companyName,
      jdData: uploadedFiles.jd.scrapedData,
      resumeData: uploadedFiles.resume.scrapedData,
      topic: Object.keys(chipsData)[selectedChipIndex],
      difficulty: selectedDifficulty
    });
  };

  const isFormValid = () => {
    return uploadedFiles.jd && uploadedFiles.resume && companyName && selectedChipIndex !== null;
  };
  
  const fetchCompanies = (async() => {
    try {
      if(!companyName.length) return;
  
      const result = await service.callService(
        getCompaniesWithDetails, {name: companyName}
      );
  
      setCompaniesData(Object.values(result));
    } catch (error) {
      console.log(error);
    }
  });

  React.useEffect(() => {
    if(companyName.length){
      fetchCompanies();
    }else{
      setCompaniesData([]);
    }
  }, [companyName]);

  useEffect(() => {
    if (openDialog) {
      const timer = setTimeout(() => {
        setOpenDialog(false);
        handleCreate();
        // setActiveStep && setActiveStep(1);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [openDialog]);

  return (
    <Box className={classes.createContainer}>
      <Box display='flex' flexDirection='column' gap='8px'>
        <Typography variant='h4-medium'>Create Challenge</Typography>
        <Typography variant='h6-medium' color='neutral.clr-700'>
          Based on company’s job description and your resume
        </Typography>
      </Box>
      
      <TextField
        name="companyName"
        label="Company name"
        style={{ width: '100%' }}
        placeholder="E.g. Google"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
      />

      {
        (!companiesData.length || !companyName.length) ? null :
        <Box>
          <Box className={classes.companies}>
            {companiesData.map((data, i)=> 
              <CompanyCard
                key={i}
                {...data}
                subTopics={data?.subTopics?.length}
              /> 
            )}
          </Box>
          <Typography variant='h6-semiBold' color='success.clr-500'>
            This company already exists!
          </Typography>
        </Box>
      }
      <Box
        className={classes.detailsSection}
        style={(!companiesData.length || !companyName.length) ? null : { opacity: '20%'}}
      >
        <Box className={classes.uploadsRow}>
          {/* Job Description Upload */}
          <FileUploadSection
            fileType="jd"
            title="Company's job description"
            iconSrc="https://assets.languify.in/images/jdDoc.png"
            onFileProcessed={handleFileProcessed}
            uploadName="jobDescription"
            changeText="job description"
          />

          {/* Resume Upload */}
          <FileUploadSection
            fileType="resume"
            title="Your resume"
            iconSrc="https://assets.languify.in/images/resumeDoc.png"
            onFileProcessed={handleFileProcessed}
            uploadName="resume"
            changeText="resume"
          />
        </Box>
        
        <Box display='flex' flexDirection='column' gap='8px'>
          <Typography variant='body01-bold'>Select one DSA topic</Typography>
          {loading ? (
            <Box>
              <Skeleton variant="rectangular" width="100%" height='30px' />
            </Box>
          ) : (
            <Box className={classes.topicContainer}>
              {Object.keys(chipsData).map((chip, index) => {
                const isSelected = selectedChipIndex === index;
                const chipLabel = capitalizeFirstLetter(chip);
                
                return (
                  <Box
                    key={index}
                    className={classes.chip}
                    onClick={() => handleChipClick(index)}
                    sx={{
                      backgroundColor: isSelected ? 'primary.main' : 'transparent',
                      color: isSelected ? 'shades.clr-white-900' : 'primary.main',
                    }}
                  >
                    <TitleTooltip
                      placement='bottom'
                      component={chipLabel}
                      disableHoverListener={chipLabel?.length < 2}
                      positionStyle={{ top: '-10px !important', left: '0px !important', zIndex: 5 }}
                    >
                      <Typography
                        variant='body01-bold'
                        sx={{ 
                          color: isSelected ? 'common.white' : 'primary.main',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}
                      >
                        {chipLabel}
                      </Typography>
                    </TitleTooltip>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box className={classes.bottomBox}>
          <SimpleSelect
            name="difficulty"
            options={difficultyOptions}
            label="Difficulty (optional)"
            style={{ width: '700px' }}
            placeholder="Select option"
            onSelect={(data) => setDifficulty(data)}
            value={difficulty}
            disabled={!isFormValid()}
          />

          <NoAccessTooltip show={removed} fullWidth>
            <CustomButton
              sx={{ width: '100%' }}
              onClick={()=> setOpenDialog(true)}
              disabled={!isFormValid() || !difficulty || removed}
              size='medium'
            >
              Create
            </CustomButton>
          </NoAccessTooltip>
        </Box>

        {
          (!companiesData.length || !companyName.length) 
            ? null : <Box className={classes.blurDetails}></Box>
        }
      </Box>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            width: '336px',
            height: '228px',
            borderRadius: '8px'
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 17, 38, 0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          },
        }}
        open={openDialog}
      >
        <Box className={classes.dialogContainer}>
          <img
            src='https://assets.languify.in/images/aiAssessment.gif'
            alt='gif'
            width={108}
            height={108}
          />
          <Typography variant='h6-medium' color='neutral.clr-900'>
            Analyzing your JD & Resume
          </Typography>
        </Box>
      </Dialog>

      <StartAssessmentDialog
        onClose={() => setStartInterview(false)}
        open={startInterview}
        template={{ _id : challenge._id , name : challenge.title}}
        codingInterview={true}
        stateParams={{data: AssessmentEntryPoint.PERSONALIZED, tagId: challenge._id}}
      />
    </Box>
  )
}

const CreatePersonalizedChallenge = ({ handleBack = () => {} }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <ArrowBackIcon style={{ cursor: 'pointer', color: '#0062E1' }} onClick={handleBack} />
          <Typography variant='h5-medium'>Personalized coding challenge</Typography>
        </Box>
      </Box>
      <Box className={classes.container}>
        <PersonalizedDetailsForm setActiveStep={setActiveStep} />
      </Box>
    </Box>
  )
}

export default CreatePersonalizedChallenge